/**
 * TODO: Replace this local IntegrationCard with @fluentsms/agentnet-web-components once
 * the MUI and React version upgrade is complete. AgentNetPortal currently uses Material-UI v4
 * and React 17, while lib-agentnet targets MUI v7 and React 19, so the shared component
 * cannot be used directly today. Vahe is working on the upgrade, but it will take time;
 * once that work lands, switch back to the lib-agentnet IntegrationCard.
 */
import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, Card, CardContent, Chip, makeStyles, Theme, Typography } from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import clsx from 'clsx';
import type { FC, ReactElement, ReactNode } from 'react';

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '6px',
    boxShadow: 'none',
    maxWidth: '378px',
    position: 'relative',
    padding: theme.spacing(3),
    width: '100%',
  },
  cardActive: {
    cursor: 'pointer',
  },
  cardFullWidth: {
    maxWidth: 'none',
  },
  header: {
    alignItems: 'flex-start',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  iconContainer: {
    alignItems: 'center',
    color: theme.palette.primary.main,
    display: 'flex',
    height: pxToRem(50),
    justifyContent: 'center',
    width: pxToRem(50),
  },
  enabledText: {
    alignItems: 'center',
    color: theme.palette.success.main,
    display: 'flex',
    fontSize: pxToRem(13),
    fontWeight: 600,
    lineHeight: pxToRem(22),
    letterSpacing: 0.46,
  },
  checkIcon: {
    marginLeft: theme.spacing(1.5),
    fontSize: pxToRem(18),
  },
  cardContent: {
    padding: 0,
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  title: {
    marginBottom: theme.spacing(1),
    fontSize: pxToRem(18),
    fontWeight: 500,
  },
  description: {
    color: theme.palette.text.secondary,
    fontSize: '14px',
    lineHeight: 1.5,
    marginBottom: theme.spacing(2),
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': 3,
    whiteSpace: 'normal',
    overflow: 'hidden',
    '&::after': {
      content: '""',
    },
  },
  categoryChip: {
    backgroundColor: theme.palette.brand.sky50,
    color: theme.palette.text.primary,
    height: pxToRem(32),
    fontSize: pxToRem(14),
  },
  statusChipsContainer: {
    alignItems: 'flex-end',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  successChip: {
    backgroundColor: '#E8F5E9',
    color: theme.palette.success.dark,
    fontSize: pxToRem(12),
    '& .MuiChip-icon': {
      color: theme.palette.success.dark,
    },
  },
  warningChip: {
    backgroundColor: '#FFF3E0',
    color: theme.palette.warning.dark,
    fontSize: pxToRem(12),
    '& .MuiChip-icon': {
      color: theme.palette.warning.dark,
    },
  },
}));

export type StatusChipVariant = 'success' | 'warning';

export interface StatusChipConfig {
  label: string;
  variant?: StatusChipVariant;
  icon?: ReactElement;
  testId?: string;
}

interface IntegrationCardProps {
  description: string;
  category: string;
  enabled: boolean;
  fullWidth?: boolean;
  icon: ReactNode;
  onClick?: () => void;
  statusChips?: StatusChipConfig[];
  title: string;
  testId?: string;
  className?: string;
}

const renderDefaultStatus = (enabled: boolean, classes: ReturnType<typeof useStyles>) => {
  if (!enabled) {
    return null;
  }

  return (
    <Typography className={classes.enabledText}>
      Enabled <CheckCircleOutlineIcon className={classes.checkIcon} />
    </Typography>
  );
};

const renderStatusChips = (chips: StatusChipConfig[], classes: ReturnType<typeof useStyles>) => (
  <Box className={classes.statusChipsContainer}>
    {chips.map((chip, index) => {
      const chipClassName = chip.variant === 'warning' ? classes.warningChip : classes.successChip;

      return (
        <Chip
          key={chip.testId ?? `${chip.label}-${index}`}
          className={chipClassName}
          label={chip.label}
          {...(chip.icon ? { icon: chip.icon } : {})}
          size="small"
          data-testid={chip.testId}
        />
      );
    })}
  </Box>
);

export const IntegrationCard: FC<IntegrationCardProps> = ({
  category,
  description,
  enabled,
  fullWidth,
  icon,
  onClick,
  statusChips,
  title,
  testId,
  className,
}) => {
  const classes = useStyles();

  return (
    <Card
      className={clsx(classes.card, onClick && classes.cardActive, fullWidth && classes.cardFullWidth, className)}
      onClick={onClick}
      data-testid={testId}
    >
      <Box className={classes.header}>
        <Box className={classes.iconContainer}>{icon}</Box>
        {statusChips?.length ? renderStatusChips(statusChips, classes) : renderDefaultStatus(enabled, classes)}
      </Box>

      <CardContent className={classes.cardContent}>
        <Typography className={classes.title} variant="h3">
          {title}
        </Typography>
        <Typography className={classes.description} variant="body1">
          {description}
        </Typography>
        <Box mt={1}>
          <Chip label={category} className={classes.categoryChip} size="medium" />
        </Box>
      </CardContent>
    </Card>
  );
};
